import React, { useEffect, useState } from 'react'
import { Navigate } from '@tanstack/react-router'
import { UserServiceUserType } from '@/types/user'
import { useAuthServiceKratosMeLazyQuery } from 'zma-react-sdk/auth-service'
import { useAuth } from '@/stores/authStore'

interface ProtectedRouteProps {
  children: React.ReactNode
  requireAdmin?: boolean
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAdmin = false,
}) => {
  const auth = useAuth()
  const [isLoading, setIsLoading] = useState(true)

  const [getMe] = useAuthServiceKratosMeLazyQuery({
    onCompleted: (data) => {
      if (data?.authServiceKratosMe) {
        auth.setUser({
          id: data.authServiceKratosMe.id,
          email: data.authServiceKratosMe.email || '',
          firstName: data.authServiceKratosMe.firstName || '',
          lastName: data.authServiceKratosMe.lastName || '',
          fullName: `${data.authServiceKratosMe.firstName} ${data.authServiceKratosMe.lastName}`,
          status: data.authServiceKratosMe.status as any,
          type: data.authServiceKratosMe.type as any,
        })
      }
      setIsLoading(false)
    },
    onError: () => {
      auth.reset()
      setIsLoading(false)
    },
  })

  useEffect(() => {
    if (auth.accessToken && auth.refreshToken) {
      getMe()
    } else {
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className='flex min-h-screen items-center justify-center'>
        <div className='h-8 w-8 animate-spin rounded-full border-b-2 border-primary'></div>
      </div>
    )
  }

  // If not authenticated, redirect to sign-in with current location as redirect
  if (!auth.isAuthenticated) {
    return <Navigate to='/sign-in' replace />
  }

  // If admin access is required, check user type
  if (requireAdmin && auth.user?.type !== UserServiceUserType.Admin) {
    return <Navigate to='/' replace />
  }

  // If authenticated (and admin if required), render children
  return <>{children}</>
}

// Higher-order component for route protection
export const withAuth = <P extends object>(
  Component: React.ComponentType<P>
) => {
  const AuthenticatedComponent = (props: P) => (
    <ProtectedRoute>
      <Component {...props} />
    </ProtectedRoute>
  )

  AuthenticatedComponent.displayName = `withAuth(${Component.displayName || Component.name})`

  return AuthenticatedComponent
}
